/**
 * Shared financial formatters. Jordan's currency (JOD) has **3** decimal places
 * (fils) — formatting it with fewer silently drops money, and statutory rates
 * like SSC 14.25% lose precision when rounded to one decimal. Screens used to
 * each roll their own (some buggy); these are the canonical helpers.
 */
/**
 * Fraction digits for an ISO currency, derived from CLDR via `Intl`. Correct
 * for 3-decimal currencies (JOD, KWD, BHD, OMR, TND — fils/millimes) and
 * 0-decimal ones (JPY, KRW), not just JOD. A hardcoded `JOD ? 3 : 2` silently
 * dropped money on the other 3-decimal MENA currencies and over-padded JPY.
 *
 * Memoized per code (the `Intl.NumberFormat` construction is the hot cost).
 * JOD keeps a fast path since it's the overwhelmingly common code here.
 * Falls back to 2 for an unknown / invalid code (the Intl default).
 */
const decimalsCache = new Map<string, number>();

export function currencyDecimals(currency: string | null | undefined): number {
  if (currency === "JOD") return 3;
  if (currency === null || currency === undefined || currency === "") return 2;
  const cached = decimalsCache.get(currency);
  if (cached !== undefined) return cached;
  let digits = 2;
  try {
    const resolved = new Intl.NumberFormat("en", {
      style: "currency",
      currency,
    }).resolvedOptions().maximumFractionDigits;
    if (typeof resolved === "number") digits = resolved;
  } catch {
    // Invalid / unknown currency code → Intl throws; keep the 2dp default.
    digits = 2;
  }
  decimalsCache.set(currency, digits);
  return digits;
}

/** UI language accepted by the money formatters (matches `@linkhr/i18n` SUPPORTED_LOCALES). */
export type MoneyLocale = "en" | "ar";

/**
 * Display precision for a money value:
 *  - `"full"` — always show the currency's fraction digits (JOD → 3dp, so
 *    `"JOD 1,250.500"`). Use on detail surfaces where fils must be exact:
 *    payslips, payroll-run detail, money detail, compensation breakdowns.
 *  - `"compact"` — up to the currency's fraction digits but no trailing
 *    zeros (`min 0 / max 3` for JOD). Use on dense lists / dashboard tiles /
 *    rosters where round amounts read cleaner. Still shows fils when present
 *    (never silently truncates like the old 0dp / 2dp roster formatters did).
 *  - `"abbreviated"` — thousands and millions shortened, one decimal at most
 *    (`"JOD 203.5k"`). Only for chart headlines and axes, never as the
 *    amount itself — the exact figure stays one hover or one click away.
 */
export type MoneyStyle = "full" | "compact" | "abbreviated";

/**
 * What names the currency next to the number:
 *  - `"symbol"` (default) — whatever CLDR uses for this currency in this
 *    language, at the placement that language puts it. Arabic gets the riyal
 *    and dinar symbols after the number (`"١٬٢٥٠٫٥٠ ر.س."`); English keeps the
 *    ISO code before it (`"SAR 1,250.50"`) because English CLDR genuinely has
 *    no distinct riyal symbol. Nothing here hardcodes a glyph — U+FDFC ﷼ and
 *    the 2025 riyal sign both have patchy font coverage, and a missing
 *    codepoint renders as tofu, which is worse than the code.
 *  - `"none"` — the number alone, at the currency's precision. For dense
 *    tables/cards that name the currency once in a header or suffix it
 *    themselves. Call sites used to get this by regex-stripping a `^[A-Z]{3}\s`
 *    prefix, which silently stopped working the moment the currency moved
 *    (Arabic) or wasn't a bare code (`"US$1,234.50"` in English).
 */
export type MoneyDisplay = "symbol" | "none";

export interface FormatMoneyOptions {
  /**
   * ISO 4217 code. **Required** — there is no house currency. A default here
   * is what made every Saudi tenant's screens print JOD: the call site read
   * as complete, so nothing looked wrong. Resolve it from the record being
   * shown, or from the tenant's `payrollCurrency` when the amount has none
   * (`useMoney()` in `@linkhr/api-client` does both).
   */
  currency: string;
  /** UI language — resolves the Intl tag (`ar` → `ar-JO`, else `en-GB`). */
  locale?: MoneyLocale;
  /** Display precision. Defaults to `"compact"`. */
  style?: MoneyStyle;
  /** Currency label. Defaults to `"symbol"`; `"none"` yields the bare number. */
  display?: MoneyDisplay;
  /**
   * Negative amounts. `"standard"` (default) keeps the locale's minus sign;
   * `"accounting"` drops it and wraps the amount in parentheses, in every
   * language and display.
   */
  sign?: "standard" | "accounting";
}

export interface FormatJodOptions {
  /** UI language — resolves the Intl tag (`ar` → `ar-JO`, else `en-GB`). */
  locale?: MoneyLocale;
  /** Display precision. Defaults to `"compact"` for backward compatibility. */
  style?: MoneyStyle;
  /** Currency code driving fraction digits. Defaults to `"JOD"`. */
  currency?: string;
  /** Currency label. Defaults to `"symbol"`; `"none"` yields the bare number. */
  display?: MoneyDisplay;
}

/**
 * The per-user numerals preference, read off the shared globalThis channel that
 * `../numerals.ts` publishes. This folder is the pure `@linkhr/i18n/format`
 * subpath and must not import `numerals.ts` (it pulls in i18next), so it reads
 * the resolved mode from the well-known key instead. Absent / any-other value ⇒
 * Arabic-Indic (the default), so money keeps Arabic-Indic digits unless the user
 * explicitly picked Latin numerals. Keep the key string in sync with
 * `packages/i18n/src/numerals.ts`.
 */
function numeralsLatinSelected(): boolean {
  return (globalThis as Record<string, unknown>)["__linkhrNumeralsMode__"] === "latin";
}

/**
 * The canonical Intl tag for a UI language — mirrors `@linkhr/i18n` `intlLocale`,
 * including its numerals-preference handling: Arabic in `"latin"` mode appends
 * `-u-nu-latn` so money digits render Western while separators stay Arabic.
 */
function tagFor(locale: MoneyLocale): string {
  if (locale === "ar") return numeralsLatinSelected() ? "ar-JO-u-nu-latn" : "ar-JO";
  return "en-GB";
}

const DECIMAL_LITERAL = /^-?\d+(\.\d+)?$/;

/**
 * `prefix` + the formatted amount. For accounting negatives the minus sign Intl
 * placed is dropped and the whole run wrapped in parentheses. Done on the parts
 * rather than with `currencySign: "accounting"`: CLDR keeps the minus for
 * Arabic-Indic digits, and the decimal style has no accounting sign at all.
 */
function signed(fmt: Intl.NumberFormat, amount: number, accounting: boolean, prefix = ""): string {
  if (!accounting) return prefix + fmt.format(amount);
  const parts = fmt.formatToParts(amount);
  const text = prefix + parts.map((p) => (p.type === "minusSign" ? "" : p.value)).join("");
  return parts.some((p) => p.type === "minusSign") ? `(${text})` : text;
}

/**
 * An amount in a named currency. Fraction digits come from the currency (JOD
 * 3, SAR 2, JPY 0); the currency label, its placement, and digit shaping all
 * come from the UI language via `Intl` — so the examples below are the `"en"`
 * (en-GB) form only:
 *
 *   formatMoney(1234.5, { currency: "SAR" })                  → "SAR 1,234.5"
 *   formatMoney(1234.5, { currency: "SAR", style: "full" })   → "SAR 1,234.50"
 *   formatMoney(1234.567, { currency: "JOD", style: "full" }) → "JOD 1,234.567"
 *   formatMoney(1234.5, { currency: "USD", style: "full" })   → "US$1,234.50"
 *
 * In `"ar"` the same values render with Arabic-Indic digits, Arabic separators
 * and the Arabic currency symbol AFTER the number (`"١٬٢٣٤٫٥ ر.س."` — plus the
 * RLM marks CLDR embeds so the run keeps its order in either direction),
 * unless the user picked Latin numerals. `display: "none"` drops the label
 * entirely and returns just the number.
 *
 * `value` may also be a decimal string (`"1234.500"`, `"-0.125"`), which Intl
 * formats exactly — no float rounding on amounts past 15 significant digits.
 *
 * Non-finite input, and a string that isn't a plain decimal, renders an em dash
 * rather than "NaN".
 */
export function formatMoney(value: number | string, opts: FormatMoneyOptions): string {
  const literal = typeof value === "string" ? value.trim() : null;
  if (literal === null ? !Number.isFinite(value) : !DECIMAL_LITERAL.test(literal)) return "—";
  // Intl takes a decimal string exactly at runtime; lib is ES2022, which types it number-only.
  const amount = (literal ?? value) as number;
  const {
    currency,
    locale = "en",
    style = "compact",
    display = "symbol",
    sign = "standard",
  } = opts;
  const accounting = sign === "accounting";
  const max = currencyDecimals(currency);
  const digits =
    style === "abbreviated"
      ? ({ notation: "compact", maximumFractionDigits: 1 } as const)
      : ({
          minimumFractionDigits: style === "full" ? max : 0,
          maximumFractionDigits: max,
        } as const);
  const tag = tagFor(locale);
  if (display === "none") return signed(new Intl.NumberFormat(tag, digits), amount, accounting);
  try {
    const fmt = new Intl.NumberFormat(tag, {
      style: "currency",
      currency,
      currencyDisplay: "symbol",
      ...digits,
    });
    return signed(fmt, amount, accounting);
  } catch {
    // `style: "currency"` throws on a malformed code ("", "JODX"); the plain
    // decimal formatter doesn't. Keep naming the currency rather than printing
    // a bare number that reads as some other currency.
    return signed(new Intl.NumberFormat(tag, digits), amount, accounting, `${currency} `);
  }
}

/**
 * A JOD amount. JOD is a 3-decimal (fils) currency, so amounts are never
 * truncated to whole dinars.
 *
 * Digit shaping and the currency label follow the locale, so the examples here
 * are the `"en"` (en-GB) form only: `formatJod(1234.567, "en")` →
 * `"JOD 1,234.567"`. The `"ar"` (ar-JO) locale renders Arabic-Indic digits,
 * Arabic separators and the Arabic symbol after the number — the same value is
 * `"١٬٢٣٤٫٥٦٧ د.أ."`, NOT Latin digits and NOT a leading code.
 *
 * Second argument is either the UI language (`"en"` / `"ar"`, the legacy form
 * — always `"compact"`) or an options object selecting the display `style`,
 * `locale`, and `currency`. `"compact"` drops trailing zeros; `"full"` forces
 * the currency's fraction digits (3 for JOD).
 *
 * @deprecated Superseded by {@link formatMoney}, which takes the currency as a
 * required argument. The `"JOD"` default here is why a Saudi tenant saw dinars
 * on every screen. `scripts/check-money-currency.mjs` holds the remaining
 * defaulted callers at a ceiling that only ever goes down; when it reaches
 * zero, this function goes with it.
 */
export function formatJod(value: number, opts: MoneyLocale | FormatJodOptions = "en"): string {
  const options: FormatJodOptions = typeof opts === "string" ? { locale: opts } : opts;
  return formatMoney(value, { ...options, currency: options.currency ?? "JOD" });
}

export interface TenantCurrencyChoice {
  /** What a picker offers: the tenant's payroll currency, plus a stored foreign one. */
  options: { value: string; label: string }[];
  /** What the picker shows. `""` when there is nothing to show. */
  value: string;
  /** Nothing can be offered — jurisdiction unresolved and no currency stored. */
  unresolved: boolean;
  /** The stored currency is not the tenant's payroll currency. */
  foreign: boolean;
}

/**
 * Which currencies a tenant-scoped record may be denominated in.
 *
 * The tenant's payroll currency and nothing else. A free-text box or a fixed
 * JOD/USD/EUR list lets an admin type dinars on a Saudi tenant, which is how
 * approval chains, grade bands, project budgets and part-time rates all ended
 * up denominated in a currency their payroll never runs in.
 *
 * Two things it deliberately does NOT do:
 *   - guess while the jurisdiction is unresolved (`unresolved` → hold the
 *     control; a guessed currency is the bug this replaces), and
 *   - drop a currency a tenant already stored. An existing record stays
 *     selectable so opening a form can't silently rewrite it; `foreign` drives
 *     the warning that says why it won't save.
 */
export function tenantCurrencyChoice(
  stored: unknown,
  payrollCurrency: string | null,
): TenantCurrencyChoice {
  const tenant = payrollCurrency !== null && payrollCurrency !== "" ? payrollCurrency : null;
  const held = typeof stored === "string" && stored !== "" ? stored : null;
  const codes = tenant !== null ? [tenant] : [];
  if (held !== null && held !== tenant) codes.push(held);
  return {
    options: codes.map((c) => ({ value: c, label: c })),
    value: held ?? tenant ?? "",
    unresolved: codes.length === 0,
    foreign: held !== null && tenant !== null && held !== tenant,
  };
}

/**
 * The currencies a multi-currency field offers (a bank account, an expense
 * line, a travel estimate). Same list and order as the backend's travel
 * currency catalogue; the server accepts any ISO 4217 code on these fields.
 */
export const CURRENCY_CODES = [
  "JOD",
  "USD",
  "EUR",
  "GBP",
  "AED",
  "SAR",
  "KWD",
  "QAR",
  "BHD",
  "OMR",
  "EGP",
] as const;

/**
 * Picker options for a field that may hold any currency, labelled in the UI
 * language ("Jordanian Dinar (JOD)" / "دينار أردني (JOD)"). The tenant's own
 * currency goes first. A stored code outside the list is kept, so opening a
 * form never rewrites it. Payroll-bound fields use `tenantCurrencyChoice`.
 */
export function currencyOptions({
  locale,
  tenant,
  stored,
}: {
  locale: MoneyLocale;
  tenant: string | null;
  stored?: string | null;
}): { value: string; label: string }[] {
  const codes = new Set(
    [tenant, ...CURRENCY_CODES, stored].filter(
      (c): c is string => typeof c === "string" && c !== "",
    ),
  );
  const names = new Intl.DisplayNames([tagFor(locale)], { type: "currency" });
  return [...codes].map((code) => {
    let name = code;
    try {
      name = names.of(code) ?? code;
    } catch {
      // A malformed stored code ("JODX") throws; show it bare.
    }
    return { value: code, label: name === code ? code : `${name} (${code})` };
  });
}

/**
 * A rate fraction (0.1425) as a percent string (`"14.25%"`), at full precision
 * up to 3 decimals with no forced trailing zeros — so 14.25% stays 14.25%,
 * not 14.3%.
 */
export function formatRatePercent(rate: number, locale: MoneyLocale = "en"): string {
  if (!Number.isFinite(rate)) return "—";
  return new Intl.NumberFormat(tagFor(locale), {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(rate);
}
